'use client';

import type { ForwardRefRenderFunction, InputHTMLAttributes } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { twJoin } from 'tailwind-merge';
import {
  Item as RadixItem,
  Indicator as RadixIndicator,
} from '@radix-ui/react-radio-group';

type BaseProps = {
  invalid?: string;
  asRadix?: boolean;
};

export type RadioButtonProps = BaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, keyof BaseProps | 'type'>;

const RadioButtonRenderFunction: ForwardRefRenderFunction<
  HTMLInputElement | HTMLButtonElement | null,
  RadioButtonProps
> = ({ invalid, asRadix, ...attrs }, forwardedRef) => {
  const ref = useRef<HTMLInputElement | HTMLButtonElement | null>(null);

  useImperativeHandle<
    HTMLInputElement | HTMLButtonElement | null,
    HTMLInputElement | HTMLButtonElement | null
  >(forwardedRef, () => ref.current);

  useEffect(() => {
    if (asRadix) {
      return;
    }

    ref.current?.setCustomValidity(invalid ?? '');
  }, [ref, invalid, asRadix]);

  const baseClassName = twJoin(
    'relative size-8 flex-shrink-0 appearance-none rounded-full md:size-6',
    'border border-stroke-primary-100',
    'hover:cursor-pointer',
    'disabled:border-stroke-secondary-40 disabled:hover:cursor-not-allowed disabled:hover:bg-transparent',
    'invalid:border-stroke-error-100',
    'focus:outline-none focus-visible:shadow-focus-state',
  );

  if (asRadix) {
    return (
      <RadixItem
        ref={(innerRef: any) => (ref.current = innerRef)}
        value={(attrs.value ?? '').toString()}
        disabled={attrs.disabled}
        className={twJoin(
          baseClassName,
          'data-[state=checked]:border-transparent data-[state=checked]:bg-surface-primary-100',
        )}
      >
        <RadixIndicator className="absolute inset-2 rounded-full bg-surface-white md:inset-1.5" />
      </RadixItem>
    );
  }

  return (
    <input
      {...attrs}
      className={twJoin(
        baseClassName,
        'before:absolute before:inset-2 before:hidden before:rounded-full before:bg-surface-white md:before:inset-1.5',
        'checked:border-transparent checked:bg-surface-primary-100 checked:before:block',
      )}
      type="radio"
      ref={(innerRef: any) => (ref.current = innerRef)}
    />
  );
};

/**
 * Radio
 *
 * <Radio />
 *
 * ## Invalid Radios (Radios with Errors)
 *
 * Provide an error message in the `invalid` attribute to mark the checkbox as errored.
 *
 * ```tsx
 * <Radio invalid="You can't select this." />
 * ```
 */
export const RadioButton = forwardRef<
  HTMLInputElement | HTMLButtonElement | null,
  RadioButtonProps
>(RadioButtonRenderFunction);
